import * as React from 'react';

import { useLockFn, useMemoizedFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useCategoryAction, useCategoryValue } from 'context';
import { createCategory, updateCategory } from 'services';
import { replaceExtraSeparator } from 'utils';
import { PATHNAME } from 'utils';

import { CategoryFormAction } from './action';
import { CategoryFormTabs } from './tabs';

const {
  LOCAL: {
    CATEGORIES: { BASE_URL: CATEGORIES_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<ReturnType<typeof useCategoryValue>, 'status'>;

const CategoryForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    title = '',
    parent,
    relatedAttribute,
    subSlug,
    iconId,
    description,
    seo,
    defaultTitle,
    defaultStatus,
    mode,
    step,
    catalogBrand,
    tags = [],
  } = useCategoryValue();
  const action = useCategoryAction();
  const navigate = useNavigate();

  const formatedSubSlug = replaceExtraSeparator(subSlug ?? title);

  const handleUpdateCategoryBaseInfo = useMemoizedFn(() => {
    action({
      type: 'UPDATE_CATEGORY_STEP',
      payload: {
        step: 2,
      },
    });
  });

  const handleUpdateCategorySeoInfo = useLockFn(
    async ({ status = defaultStatus! }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updateCategory>[0] = {
        id,
      };
      const body: Parameters<typeof updateCategory>[1] = {
        title,
        iconId,
        parentId: parent?.id,
        relatedAttribute: relatedAttribute && {
          id: relatedAttribute.id,
          selectedValues: relatedAttribute.selectedValues,
        },
        subSlug: formatedSubSlug,
        catalogBrandId: catalogBrand?.id,
        tagsIds: tags.map(({ id }) => id),
        description,
        seo,
        status,
      };

      (isUpdatedMode ? updateCategory(params, body) : createCategory(body))
        .then(() => {
          notification.success({
            message: `دسته‌بندی‌ ${isUpdatedMode ? defaultTitle : title} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(CATEGORIES_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  const onFinish = useLockFn(async (fields: FieldType) => {
    switch (step) {
      case 1: {
        return handleUpdateCategoryBaseInfo();
      }
      case 2: {
        return handleUpdateCategorySeoInfo(fields);
      }
      default:
        break;
    }
  });

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'title',
          value: title,
        },
        {
          name: 'parent',
          value: parent && {
            label: parent.title,
            value: parent.id,
          },
        },
        {
          name: 'relatedAttribute',
          value: parent &&
            relatedAttribute && {
              label: relatedAttribute.name,
              value: relatedAttribute.id,
            },
        },
        {
          name: 'relatedAttributeValue',
          value:
            parent && relatedAttribute && relatedAttribute.selectedValues.at(0),
        },
        {
          name: 'catalogBrand',
          value: catalogBrand && {
            key: catalogBrand.id,
            label: catalogBrand.title,
            value: catalogBrand.id,
          },
        },
        {
          name: 'subSlug',
          value: formatedSubSlug,
        },
        {
          name: 'tags',
          value: tags.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          })),
        },
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:flex-1 *:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:max-w-[70%] xl:last:*:max-w-[30%]'
        gap='large'
      >
        <CategoryFormTabs />
        <CategoryFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { CategoryForm };
